// Renders target-language text with every word individually tappable: tap a
// word → a small popover fetches its translation into the user's app language
// via the existing /lecture/explain endpoint (kind="translate"). Used only in
// language-learner sets (gated by the caller). Non-word runs (spaces, punctuation)
// render as plain text, so the original formatting is preserved.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, cx } from "@/components/ui";
import { explain } from "@/api/lecture";
import i18n from "@/i18n";

// Split into alternating [gap, word, gap, word, …] — the capture group keeps the
// word; even indices are the surrounding whitespace/punctuation. \p{L} covers
// accented + non-Latin letters; CJK runs tap as one token (no per-char gloss).
const WORD_SPLIT = /(\p{L}[\p{L}\p{M}­'’-]*)/u;

// Session cache so re-tapping a word (or the same word elsewhere) is instant and
// free. Keyed by language + lowercased word.
const glossCache = new Map<string, string>();

/** The frameworks whose native renderings get word-tap translation.
 *  Includes de-trainer's legacy slug `de-grammar-deep-dive` so older sets
 *  (generated under the old slug) gloss too. */
export function isLanguageFramework(slug?: string | null): boolean {
  return (
    slug === "language-trainer" ||
    slug === "de-trainer" ||
    slug === "de-grammar-deep-dive"
  );
}

interface Anchor {
  word: string;
  top: number;
  left: number;
}

export function GlossableText({
  text,
  context,
  className,
}: {
  text: string;
  context?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [gloss, setGloss] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const reqId = useRef(0);

  const lang = i18n.resolvedLanguage ?? "en";

  const open = (word: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setAnchor({ word, top: rect.bottom, left: rect.left });
    const key = `${lang}|${word.toLowerCase()}`;
    const cached = glossCache.get(key);
    if (cached !== undefined) {
      setGloss(cached);
      setLoading(false);
      setError(false);
      return;
    }
    setGloss(null);
    setError(false);
    setLoading(true);
    const id = ++reqId.current;
    explain({ kind: "translate", selectedText: word, context: context ?? null, language: lang })
      .then((res) => {
        if (id !== reqId.current) return; // a newer tap superseded this one
        const value = (res.explanation || "").trim();
        glossCache.set(key, value);
        setGloss(value);
        setLoading(false);
      })
      .catch(() => {
        if (id !== reqId.current) return;
        setError(true);
        setLoading(false);
      });
  };

  const close = () => {
    setAnchor(null);
    reqId.current++; // invalidate any in-flight request
  };

  // Dismiss on outside-click, Escape, or scroll (the anchor would drift).
  useEffect(() => {
    if (!anchor) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest("[data-gloss-popover]") && !el.closest("[data-gloss-word]")) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    const onScroll = () => close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchor]);

  const parts = text.split(WORD_SPLIT);

  return (
    <>
      <span className={className}>
        {parts.map((part, i) =>
          i % 2 === 1 ? (
            // A <span> (not <button>) with role=button so it can nest inside
            // answer buttons / option rows without invalid nested-button markup;
            // stopPropagation keeps a word tap from also selecting the option.
            <span
              key={i}
              role="button"
              tabIndex={0}
              data-gloss-word
              onClick={(e) => {
                e.stopPropagation();
                open(part, e.currentTarget);
              }}
              className={cx(
                "cursor-pointer rounded-[3px] underline decoration-dotted decoration-ink-faint/40 underline-offset-2 transition-colors hover:bg-brand-100 hover:decoration-brand-400",
                anchor?.word === part && "bg-brand-100",
              )}
              title={t("Tap to translate")}
            >
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          ),
        )}
      </span>

      {anchor && (
        <div
          data-gloss-popover
          style={{ position: "fixed", top: anchor.top + 6, left: anchor.left, zIndex: 50 }}
          className="max-w-xs rounded-xl border border-black/10 bg-white px-3 py-2 text-sm shadow-soft ring-1 ring-black/5"
        >
          <div className="text-xs font-semibold text-ink-faint">{anchor.word}</div>
          {loading ? (
            <div className="mt-1 flex items-center gap-2 text-ink-faint">
              <Spinner className="h-3.5 w-3.5" /> {t("Translating…")}
            </div>
          ) : error ? (
            <div className="mt-0.5 text-red-600">{t("Couldn't translate.")}</div>
          ) : (
            <div className="mt-0.5 font-medium text-ink">{gloss || t("No translation.")}</div>
          )}
        </div>
      )}
    </>
  );
}
